/**
 * Attach the Supabase access token to every same-origin /api/* request.
 *
 * The non-Entra (Vercel) build had no equivalent of the Entra interceptor in
 * `entraAuth.ts`, so requests to the app's own API carried no caller identity
 * at all. That was survivable only because the SAP endpoints did not check for
 * one: /api/s4/* and the six /api/erp-sync/* routes accepted anonymous calls
 * from the public internet, forwarded the method verbatim, and supplied the
 * x-ppm-key credential themselves. Those endpoints now require a bearer token
 * (web/api/_requireUser.js), so the client has to send one.
 *
 * Patching fetch once is deliberate, and it is the choice this codebase already
 * made for the Entra build: there are ~40 call sites across S/4 lookups, ERP
 * sync, CLM analysis, notifications and AI, and editing each one would leave
 * the next one added unprotected. Same rules as the Entra interceptor:
 *   - same-origin /api/* only
 *   - never overwrite an Authorization header the caller already set
 *     (supabase-js sets its own on /api/db via the accessToken option)
 *   - any failure falls through to the unmodified request, so a signed-out
 *     user still gets a clean 401 from the server rather than a thrown fetch
 *
 * The two interceptors are mutually exclusive: this one installs only when the
 * Entra one does not.
 */
import { supabase } from '@/lib/supabase'
import { isEntraAuthEnabled } from '@/lib/entraAuth'

let installed = false

export function installSupabaseApiBearer(): void {
  if (installed) return
  if (typeof window === 'undefined') return
  // Entra builds already have an interceptor doing exactly this with an Entra
  // token. Two patches would stack and the second would never win.
  if (isEntraAuthEnabled()) return
  installed = true

  const nativeFetch = window.fetch.bind(window)
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    try {
      const rawUrl =
        typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
      const url = new URL(rawUrl, window.location.origin)
      if (url.origin === window.location.origin && url.pathname.startsWith('/api/')) {
        const headers = new Headers(
          init?.headers ?? (input instanceof Request ? input.headers : undefined),
        )
        if (!headers.has('Authorization')) {
          const { data } = await supabase.auth.getSession()
          const token = data.session?.access_token
          if (token) {
            headers.set('Authorization', `Bearer ${token}`)
            return nativeFetch(input, { ...init, headers })
          }
        }
      }
    } catch {
      /* fall through to the unmodified request */
    }
    return nativeFetch(input, init)
  }
}
